'use client'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { useMounted } from '@/hooks/use-mounted'

import { UserRoundX } from 'lucide-react'

import { useImpersonation } from '../hooks/use-impersonation'

interface EndImpersonationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function EndImpersonationDialog({
  open,
  onOpenChange
}: EndImpersonationDialogProps) {
  const { endImpersonation, isEnding } = useImpersonation()
  const mounted = useMounted()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <UserRoundX className="size-4" />
            End Impersonation
          </DialogTitle>
          <DialogDescription>
            Where would you like to go after ending impersonation?
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div className="flex flex-col gap-2">
            <Button
              variant="outline"
              className="h-auto w-full flex-col items-start gap-0.5 px-3 py-2"
              disabled={isEnding}
              onClick={() => {
                const originalPath = mounted
                  ? (sessionStorage.getItem('impersonation_original_path') ??
                    undefined)
                  : undefined
                onOpenChange(false)
                endImpersonation(originalPath)
              }}
            >
              <span className="text-sm font-medium">
                Return to original page
              </span>
              <span
                dir="rtl"
                className="text-muted-foreground w-full truncate text-left text-xs"
              >
                {'‎'}
                {mounted
                  ? (sessionStorage.getItem('impersonation_original_path') ??
                    'Unknown')
                  : ''}
              </span>
            </Button>
            <Button
              variant="outline"
              className="h-auto w-full flex-col items-start gap-0.5 px-3 py-2"
              disabled={isEnding}
              onClick={() => {
                const currentPath = mounted
                  ? window.location.pathname
                  : undefined
                onOpenChange(false)
                endImpersonation(currentPath)
              }}
            >
              <span className="text-sm font-medium">Stay on current page</span>
              <span
                dir="rtl"
                className="text-muted-foreground w-full truncate text-left text-xs"
              >
                {'‎'}
                {mounted ? window.location.pathname : ''}
              </span>
            </Button>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isEnding}
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
